import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, Package, ArrowRight } from 'lucide-react';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [orderData, setOrderData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`/api/payments/status/${sid}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrderData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Status check error:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background: '#0a0a0f',
        fontFamily: "'Cormorant Garamond', Georgia, serif",
      }}
    >
      <div className="max-w-lg w-full text-center">
        {status === 'checking' && (
          <div className="space-y-6">
            <Loader2 size={48} className="mx-auto text-yellow-400 animate-spin" />
            <h2 className="text-2xl text-white font-light">Confirming your order...</h2>
            <p className="text-gray-500 text-sm">This will only take a moment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full">
              <CheckCircle size={48} className="text-green-400" />
            </div>
            <div>
              <h2 className="text-3xl text-white font-light mb-2">Thank You!</h2>
              <p className="text-yellow-400 text-lg">{orderData?.package_name || 'Your order is confirmed'}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-left space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Amount</span>
                <span className="text-white" data-testid="success-amount">${orderData?.amount?.toFixed(2) || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Status</span>
                <span className="text-green-400" data-testid="success-status">Paid</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Order #</span>
                <span className="text-gray-300 text-xs font-mono" data-testid="success-order-id">{orderData?.order_id ? orderData.order_id.slice(0, 8).toUpperCase() : `${sessionId?.slice(0, 12)}...`}</span>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-yellow-400/20">
              <Package size={24} className="mx-auto text-yellow-400 mb-3" />
              <p className="text-gray-300 text-sm">
                A confirmation email is on its way. Your book is printed on demand — expected delivery is approximately <span className="text-yellow-400">10–14 days</span>. You'll get a tracking link the moment it ships.
              </p>
            </div>
            <p className="text-gray-600 text-xs italic">
              "Plant with intention. Water with consistency. Harvest with gratitude."
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {orderData?.order_id && (
                <button
                  onClick={() => navigate(`/book-of-colorz/order-status?order_id=${orderData.order_id}${orderData.customer_email ? `&email=${encodeURIComponent(orderData.customer_email)}` : ''}`)}
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-black rounded-full hover:opacity-90 transition-all font-medium"
                  data-testid="success-track-btn"
                >
                  <span>Track My Order</span>
                  <ArrowRight size={16} />
                </button>
              )}
              <button
                onClick={() => navigate('/book-of-colorz')}
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white/10 border border-yellow-400/30 text-yellow-400 rounded-full hover:bg-white/20 transition-all"
              >
                <span>Return to Book of Colorz</span>
              </button>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div className="space-y-6">
            <h2 className="text-2xl text-white font-light">
              {status === 'expired' ? 'Session Expired' : 'Something went wrong'}
            </h2>
            <p className="text-gray-400 text-sm">
              {status === 'expired'
                ? 'Your checkout session has expired. Please try again.'
                : 'We could not confirm your payment. If you were charged, please contact support@bookofcolorz.com'}
            </p>
            <button
              onClick={() => navigate('/book-of-colorz')}
              className="px-6 py-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors"
            >
              Return to Book of Colorz
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
